import { useEffect, useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminAccessDenied } from "@/components/admin/AdminAccessDenied";
import { AdminLoading, useAdminAuth } from "@/components/admin/useAdminAuth";
import { formatDate, STATUS_LABELS } from "@/components/candidate/shared";

const API = process.env.REACT_APP_BACKEND_URL;

const PROFILE_LABELS = {
    phone: "Phone / WhatsApp",
    country: "Country",
    city: "City",
    nationality: "Nationality",
    headline: "Professional headline",
    profession: "Profession",
    current_occupation: "Current occupation",
    years_experience: "Years of experience",
    skills: "Skills",
    industries: "Industries",
    preferred_countries: "Preferred countries",
    preferred_job_types: "Preferred job types",
    preferred_salary: "Preferred salary",
    education_level: "Education level",
    institution: "Institution",
    field_of_study: "Field of study",
    graduation_year: "Graduation year",
    certifications: "Certifications",
    licences: "Professional licences",
    other_qualifications: "Other qualifications",
};

export default function AdminCandidates() {
    const { user, logout, hasPermission } = useAdminAuth();
    const [candidates, setCandidates] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!user) return;
        fetch(`${API}/api/admin/candidates`, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : []))
            .then(setCandidates)
            .catch(() => setCandidates([]));
    }, [user]);

    useEffect(() => {
        if (!candidates) return;
        const open = new URLSearchParams(window.location.search).get("open");
        if (open) openCandidate(open);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidates]);

    const openCandidate = async (id) => {
        const res = await fetch(`${API}/api/admin/candidates/${id}`, { credentials: "include" });
        if (res.ok) setSelected(await res.json());
    };

    const download = async (stored, original) => {
        const res = await fetch(`${API}/api/admin/candidates/${selected.id}/documents/${stored}`, { credentials: "include" });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = original;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!user) return <AdminLoading />;
    if (!hasPermission("candidates")) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] text-ink">
                <AdminNav active="candidates" email={user.email} user={user} onLogout={logout} />
                <AdminAccessDenied section="candidates" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ivory text-ink">
            <AdminNav active="candidates" email={user.email} user={user} onLogout={logout} />

            <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <h1 className="font-serif text-3xl font-medium tracking-tight">Candidates</h1>
                <p className="mt-1 text-sm text-ink/55">
                    Registered candidate accounts, their profiles and application activity.
                </p>

                <div className="mt-10" data-testid="admin-candidates-list">
                    {candidates === null ? (
                        <div className="flex justify-center py-20"><Loader2 size={22} className="animate-spin text-forest" /></div>
                    ) : candidates.length === 0 ? (
                        <p className="border-t border-ink/10 py-16 text-center text-sm text-ink/50">
                            No candidate accounts yet.
                        </p>
                    ) : (
                        candidates.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                data-testid={`candidate-row-${c.id}`}
                                onClick={() => openCandidate(c.id)}
                                className="group grid w-full grid-cols-[1fr_auto] items-center gap-x-6 gap-y-1 border-t border-ink/10 px-2 py-5 text-left transition-colors hover:bg-sand sm:grid-cols-[1fr_auto_auto_auto_auto]"
                            >
                                <span className="min-w-0">
                                    <span className="block truncate font-serif text-lg font-medium tracking-tight text-ink">
                                        {c.name}
                                        {c.deactivated && (
                                            <span className="ml-2 border border-ink/15 px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] text-ink/40">Deactivated</span>
                                        )}
                                    </span>
                                    <span className="block truncate text-xs text-ink/50">
                                        {c.email}{c.profession ? ` · ${c.profession}` : ""}{c.country ? ` · ${c.country}` : ""}
                                    </span>
                                </span>
                                <span className="hidden text-xs text-ink/50 sm:block">{c.applications} application{c.applications === 1 ? "" : "s"}</span>
                                <span className="hidden text-xs text-ink/50 sm:block">Profile {c.completion}%</span>
                                <span className="hidden text-xs text-ink/50 sm:block">{c.documents} doc{c.documents === 1 ? "" : "s"}</span>
                                <span className="text-forest opacity-0 transition-opacity group-hover:opacity-100">→</span>
                            </button>
                        ))
                    )}
                    <div className="border-t border-ink/10" />
                </div>
            </div>

            {selected && (
                <div className="fixed inset-0 z-50" data-testid="candidate-detail">
                    <div className="absolute inset-0 bg-night/50" onClick={() => setSelected(null)} />
                    <div className="absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto bg-ivory p-7 sm:p-10">
                        <div className="flex items-start justify-between gap-6">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-ink/45">
                                    Candidate profile · {selected.completion}% complete
                                </p>
                                <h2 className="mt-2 font-serif text-2xl font-medium tracking-tight">{selected.name}</h2>
                                <p className="mt-1 text-sm text-ink/55">{selected.email}</p>
                            </div>
                            <button type="button" data-testid="candidate-detail-close" onClick={() => setSelected(null)} aria-label="Close" className="border border-ink/15 p-2 text-ink/60 transition-colors hover:border-forest hover:text-forest">
                                <X size={18} strokeWidth={1.5} />
                            </button>
                        </div>

                        <h3 className="mt-8 text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/45">Profile</h3>
                        <div className="mt-3 border-t border-ink/10">
                            {Object.entries(PROFILE_LABELS).map(([key, label]) =>
                                selected.profile[key] ? (
                                    <div key={key} className="grid grid-cols-[140px_1fr] gap-4 border-b border-ink/10 py-3 sm:grid-cols-[170px_1fr]">
                                        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">{label}</span>
                                        <span className="break-words text-sm text-ink/80">{selected.profile[key]}</span>
                                    </div>
                                ) : null
                            )}
                        </div>

                        <h3 className="mt-8 text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/45">Documents</h3>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {selected.profile.cv?.stored && (
                                <button type="button" data-testid="admin-candidate-cv" onClick={() => download(selected.profile.cv.stored, selected.profile.cv.original)} className="inline-flex items-center gap-2 border border-forest/30 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:bg-forest hover:text-ivory">
                                    <Download size={14} strokeWidth={1.5} />
                                    CV — {selected.profile.cv.original}
                                </button>
                            )}
                            {(selected.profile.documents || []).map((doc) => (
                                <button key={doc.stored} type="button" onClick={() => download(doc.stored, doc.original)} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-ink/60 transition-colors hover:border-forest hover:text-forest">
                                    <Download size={14} strokeWidth={1.5} />
                                    {doc.original}
                                </button>
                            ))}
                            {!selected.profile.cv && (selected.profile.documents || []).length === 0 && (
                                <p className="text-sm text-ink/45">No documents uploaded.</p>
                            )}
                        </div>

                        <h3 className="mt-8 text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/45">Applications</h3>
                        <div className="mt-3">
                            {selected.applications.length === 0 ? (
                                <p className="text-sm text-ink/45">No applications.</p>
                            ) : (
                                selected.applications.map((a) => (
                                    <div key={a.id} className="flex items-center justify-between gap-4 border-t border-ink/10 py-3.5">
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-medium text-ink">{a.job_title}</p>
                                            <p className="text-xs text-ink/45">{a.reference} · {formatDate(a.created_at)}</p>
                                        </div>
                                        <span className="shrink-0 border border-ink/20 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/60">
                                            {STATUS_LABELS[a.status] || a.status}
                                        </span>
                                    </div>
                                ))
                            )}
                            <div className="border-t border-ink/10" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
